import type { CourseBuilderFormState } from "@store/slices/courseBuilderSlice";
import type { CourseListApiItem, MatricStream } from "../types";
import { StudentMinimumQualification } from "../types";
import {
	parseCourseAttachmentsFromCourse,
} from "./courseAttachmentUtils";
import { collectSubjectsFromCoursePayload } from "./collectCourseContentSubjects";
import { collectAssessmentBodiesFromCoursePayload } from "./collectAssessmentBodiesFromCourse";
import { collectCompetencyPointsFromCoursePayload } from "./collectCompetencyPointsFromCourse";
import {
	calculatePromotionStages,
	resolveExamSystemDisplayValue,
	resolveExamSystemFormValue,
} from "@features/CourseBuilder/utils/courseTermUtils";
import { normalizeCourseFormDurationUnit } from "../constants/durationUnits";
import { normalizeCourseEquivalence } from "../constants/courseEquivalence";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const MATRIC_STREAM_VALUES: MatricStream[] = ["arts", "science", "both"];

function normalizeMatricStream(value: string | null | undefined): string {
	if (!value || typeof value !== "string") return "";
	const lower = value.toLowerCase().trim();
	return MATRIC_STREAM_VALUES.includes(lower as MatricStream) ? lower : "";
}

/** Map API studentMinimumQualification string to form enum label if possible */
function normalizeStudentMinimumQualification(raw: string | null | undefined): string {
	if (!raw?.trim()) return "";
	const v = raw.trim();
	const match = Object.values(StudentMinimumQualification).find(enumVal => enumVal.toLowerCase() === v.toLowerCase());
	return match ?? v;
}

/** API may send boolean, strings ("true"/"false"), or 0/1. true → Regular, false → Non-Regular. */
export function normalizeIsRegular(value: unknown): boolean {
	if (value === true || value === "true" || value === 1 || value === "1") return true;
	if (value === false || value === "false" || value === 0 || value === "0") return false;
	return true;
}

function readIsRegularFromCourse(course: CourseListApiItem): unknown {
	const r = course as Record<string, unknown>;
	return course.isRegular ?? r.regular ?? r.is_regular;
}

/** List/detail row: true → Regular, false → Non-Regular (handles API variants). */
export function isListCourseRegular(course: CourseListApiItem | Record<string, unknown>): boolean {
	return normalizeIsRegular(readIsRegularFromCourse(course as CourseListApiItem));
}

/** API may send boolean, strings, or 0/1. Inactive only when explicitly false-like. */
export function normalizeIsActive(value: unknown): boolean {
	if (value === false || value === "false" || value === 0 || value === "0") return false;
	return true;
}

export function isListCourseActive(course: CourseListApiItem | Record<string, unknown>): boolean {
	const r = course as Record<string, unknown>;
	return normalizeIsActive((course as CourseListApiItem).isActive ?? r.is_active);
}

/** API often sends mark percents as "70.00"; form/validation expect whole numbers. */
function normalizeMarksPercent(value: number | string | null | undefined): string {
	if (value == null || value === "") return "";
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return String(value).trim();
	return String(Math.round(parsed));
}

function resolveCourseNotifyingAuthorityId(course: CourseListApiItem): string {
	const fromNested = course.courseNotifyingAuthority?.uuid?.trim();
	if (fromNested && UUID_RE.test(fromNested)) return fromNested;

	const record = course as Record<string, unknown>;
	const alt = record.courseNotifyingAuthorityUuid;
	if (typeof alt === "string" && UUID_RE.test(alt.trim())) return alt.trim();

	return fromNested ?? "";
}

function normalizeNotificationDate(value: unknown): string {
	if (value == null || value === "") return "";
	const text = String(value).trim();
	if (!text) return "";
	return text.split("T")[0] ?? text;
}

/**
 * Maps a course row from GET /institutes/course (list) into full course builder Redux state.
 * List items include nested teachingMode, courseStream, courseLabs, matricStream, isRegular, etc.
 */
export function mapListCourseApiToBuilderState(course: CourseListApiItem): CourseBuilderFormState {
	const courseStreamId = course.courseStreamId ?? course.courseStream?.id;
	const teachingModeId = course.teachingModeId ?? course.teachingMode?.id;
	const sectorId = course.sectorId ?? course.sector?.id;
	const categoryId = course.courseCategoryId ?? course.courseCategory?.id;
	const isRegular = isListCourseRegular(course);
	const examSystemId = resolveExamSystemFormValue(course.examSystem, course.examSystemId);
	const duration = course.duration != null ? String(course.duration) : "";
	const durationUnit = normalizeCourseFormDurationUnit(course.durationUnit);
	const examSystemName = resolveExamSystemDisplayValue(course.examSystem);
	const calculatedPromotionStages = calculatePromotionStages(duration, durationUnit, examSystemName);
	const termCount =
		course.termCount != null && String(course.termCount).trim() ? String(course.termCount) : "";
	const subjectRefs = collectSubjectsFromCoursePayload(course);
	const subjectItems = subjectRefs.map(subject => ({
		uuid: subject.uuid,
		name: subject.name?.trim() || subject.uuid,
		...(subject.description?.trim() ? { description: subject.description } : {}),
	}));
	const subjectIds = subjectItems.map(item => item.uuid);
	const assessmentBodyRefs = collectAssessmentBodiesFromCoursePayload(course);
	const assessmentBodyItems = assessmentBodyRefs.map(body => ({
		uuid: body.uuid,
		name: body.name?.trim() || body.uuid,
	}));
	const assessmentBodyIds = assessmentBodyItems.map(item => item.uuid);

	const competencyPointRefs = collectCompetencyPointsFromCoursePayload(course);
	const competencyPointItems = competencyPointRefs.map(point => ({
		uuid: point.uuid,
		name: point.name,
	}));
	const competencyPointIds = competencyPointItems.map(item => item.uuid);

	const courseDetails: CourseBuilderFormState["courseDetails"] = {
		courseName: course.name ?? "",
		courseType: courseStreamId != null ? String(courseStreamId) : "",
		courseLevel: course.level ?? "",
		duration,
		durationUnit,
		totalStudentsCount: typeof course.totalStudentsCount === "number" ? course.totalStudentsCount : 0,
		sector: sectorId != null ? String(sectorId) : "",
		sectorName: course.sector?.name?.trim() ?? "",
		teachingMode: teachingModeId != null ? String(teachingModeId) : "",
		courseNotifyingAuthorityId: resolveCourseNotifyingAuthorityId(course),
		description: course.description ?? "",
		overview: course.overview ?? "",
		lastReview: normalizeNotificationDate(course.lastReview),
		eligibilityCriteria: course.eligibilityCriteria ?? "",
		courseMode: isRegular ? "true" : "false",
		courseCategoryId: categoryId != null ? String(categoryId) : "",
		courseCategoryName: course.courseCategory?.name?.trim() ?? "",
		subjectIds,
		subjectItems,
		assessmentBodyIds,
		assessmentBodyItems,
		studentMinimumQualification: normalizeStudentMinimumQualification(course.studentMinimumQualification),
		matricStream: normalizeMatricStream(course.matricStream),
		totalMarks: course.totalMarks != null ? String(course.totalMarks) : "",
		practicalMarksPercent: normalizeMarksPercent(course.practicalMarksPercent),
		theoreticalMarksPercent: normalizeMarksPercent(course.theoreticalMarksPercent),
		totalModules: course.totalModules != null ? String(course.totalModules) : "",
		examSystemId,
		promotionStages: calculatedPromotionStages != null ? String(calculatedPromotionStages) : "",
		termCount,
		hasOjt: (() => {
			if (course.hasOjt === true) return true;
			const contentBlocks = (course as { contents?: Array<{ hasOjt?: boolean }> }).contents ?? [];
			return contentBlocks.some(block => block.hasOjt === true);
		})(),
		ojtDuration: (() => {
			if (course.ojtDuration != null && Number.isFinite(Number(course.ojtDuration))) {
				return String(course.ojtDuration);
			}
			const contentBlocks =
				(course as { contents?: Array<{ hasOjt?: boolean; duration?: number | null }> }).contents ?? [];
			const ojtBlock = contentBlocks.find(
				block => block.hasOjt === true && block.duration != null && Number.isFinite(Number(block.duration)),
			);
			return ojtBlock?.duration != null ? String(ojtBlock.duration) : "";
		})(),
		courseCode: course.courseCode?.trim() ?? "",
		trainingHours: course.trainingHours != null ? String(course.trainingHours) : "",
		notificationDate: normalizeNotificationDate(course.notificationDate),
		version: course.version?.trim() ?? "",
		courseEquivalence: normalizeCourseEquivalence(course.courseEquivalence),
		competencyPointIds,
		competencyPointItems,
		minAge: (() => {
			const courseRecord = course as CourseListApiItem & { minAge?: number | null; minimumAge?: number | null };
			const value = courseRecord.minimumAge ?? courseRecord.minAge;
			return value != null && Number.isFinite(Number(value)) ? String(value) : "";
		})(),
		maxAge: (() => {
			const courseRecord = course as CourseListApiItem & { maxAge?: number | null; maximumAge?: number | null };
			const value = courseRecord.maximumAge ?? courseRecord.maxAge;
			return value != null && Number.isFinite(Number(value)) ? String(value) : "";
		})(),
		relaxationPeriod:
			course.relaxationPeriod != null && Number.isFinite(Number(course.relaxationPeriod))
				? String(course.relaxationPeriod)
				: "",
	};

	const laboratories: CourseBuilderFormState["laboratories"] = {
		laboratories: (course.courseLabs ?? []).map(entry => ({
			id: entry.lab?.uuid ?? String(entry.labId),
			labName: entry.lab?.name ?? "",
			numericId: entry.lab?.id ?? entry.labId,
			equipments: [],
		})),
	};

	const facultyQualificationItems = (() => {
		const collected: Array<{ uuid: string; name: string }> = [];
		const seen = new Set<string>();
		const pushItem = (uuid?: string | null, name?: string | null) => {
			const trimmed = uuid?.trim();
			if (!trimmed || seen.has(trimmed)) return;
			seen.add(trimmed);
			collected.push({ uuid: trimmed, name: name?.trim() || trimmed });
		};

		(course.facultyQualifications ?? []).forEach(item => pushItem(item.uuid, item.name));
		pushItem(course.facultyQualification?.uuid, course.facultyQualification?.name);

		const pushId = (id?: string | null) => {
			const trimmed = id?.trim();
			if (trimmed) pushItem(trimmed, trimmed);
		};

		const raw = course.facultyQualificationId;
		if (Array.isArray(raw)) {
			raw.forEach(id => {
				if (typeof id === "string") pushId(id);
			});
		} else if (typeof raw === "string") {
			pushId(raw);
		}

		if (Array.isArray(course.facultyQualificationIds)) {
			course.facultyQualificationIds.forEach(id => {
				if (typeof id === "string") pushId(id);
			});
		}

		return collected;
	})();

	const facultyDetails: CourseBuilderFormState["facultyDetails"] = {
		facultyQualificationId: facultyQualificationItems.map(item => item.uuid),
		facultyQualificationItems,
		facultyBriefRequirements: (() => {
			const courseRecord = course as CourseListApiItem & {
				facultyBriefRequirements?: string | null;
				facultyRequirements?: string | null;
			};
			const value = courseRecord.facultyRequirements ?? courseRecord.facultyBriefRequirements;
			return typeof value === "string" ? value : "";
		})(),
		facultyExperienceArea: (() => {
			const courseRecord = course as CourseListApiItem & {
				facultyExperienceArea?: string | null;
				experienceArea?: string | null;
			};
			const value = courseRecord.experienceArea ?? courseRecord.facultyExperienceArea;
			return typeof value === "string" ? value : "";
		})(),
		facultyExperienceYears: (() => {
			const courseRecord = course as CourseListApiItem & {
				facultyExperienceYears?: number | null;
				experienceYears?: number | null;
			};
			const value = courseRecord.experienceYears ?? courseRecord.facultyExperienceYears;
			return typeof value === "number" && Number.isFinite(value) ? value : 0;
		})(),
		totalFacultyMemberCount: typeof course.totalFacultyMemberCount === "number" ? course.totalFacultyMemberCount : 0,
	};

	const attachments: CourseBuilderFormState["attachments"] = {
		items: parseCourseAttachmentsFromCourse(course),
	};

	return {
		courseDetails,
		laboratories,
		facultyDetails,
		attachments,
		currentStep: 0,
		isFormValid: false,
	};
}
